import { cva } from 'class-variance-authority';
import { Divider, Flex, Icon, Text } from '@nula/core-ui';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './surface-new-face.module.css';

interface SurfaceNewFaceProps {
  onCreate: (surface: 'text' | 'canvas') => void;
  onBack: () => void;
  /** Suppressed on the empty state — there is no surfaces list to go back to. */
  showBack?: boolean;
  /** Fired when Audio is chosen. Absent means the item is not offered. */
  onRecord?: () => void;
  /** Whether the document can still start a recording (one per document —
   *  ui-design.md §9.1). Absent (never disabled) once it already has one. */
  canRecord?: boolean;
  /** Swaps to the Templates face. Absent means the doorway row is not offered. */
  onTemplates?: () => void;
  templateCount?: number;
}

const back = cva(styles.back);
const list = cva(styles.list);
const item = cva(styles.item);

/**
 * Only two buildable kinds ship here — see the milestone's deferral section
 * for why recording is not rendered, not even disabled.
 */
const ITEMS: ReadonlyArray<{ surface: 'text' | 'canvas'; label: string; kind: string }> = [
  { surface: 'text', label: 'Document', kind: 'document' },
  { surface: 'canvas', label: 'Canvas', kind: 'canvas' },
];

export function SurfaceNewFace({
  onCreate, onBack, showBack = true, onRecord, canRecord = false, onTemplates, templateCount = 0,
}: SurfaceNewFaceProps) {
  // Not gated on isDesktop: an audio surface is where a recording lives, not
  // the act of recording. Web can create one, hold it and read its transcript;
  // only starting a capture needs the desktop app, and SurfaceAudioBody says
  // so in the surface itself rather than hiding the whole kind here.
  const offerRecord = canRecord;
  return (
    <>
      {showBack && (
        <button
          type="button"
          role="menuitem"
          aria-label="Back to surfaces"
          onClick={onBack}
          className={back()}
        >
          <Flex align="center" justify="center" className={styles.backIcon} aria-hidden="true">
            <Icon name="arrow_back" size={16} />
          </Flex>
          <Text className={styles.title}>New</Text>
        </button>
      )}
      {!showBack && <Text className={styles.standaloneTitle}>New</Text>}
      <Flex vertical className={list()}>
        {ITEMS.map(({ surface, label, kind }) => (
          <button
            key={surface}
            type="button"
            role="menuitem"
            onClick={() => onCreate(surface)}
            className={item()}
          >
            <Icon name={getEntityKindMeta(kind).icon} size={18} />
            <Text>{label}</Text>
          </button>
        ))}
        {offerRecord && (
          <button
            type="button"
            role="menuitem"
            onClick={() => onRecord?.()}
            className={item()}
          >
            <Icon name={getEntityKindMeta('audio').icon} size={18} />
            <Text>Audio</Text>
          </button>
        )}
        {onTemplates && <Divider className={styles.rule} />}
        {onTemplates && (
          <button
            type="button"
            role="menuitem"
            onClick={onTemplates}
            className={item()}
          >
            <Icon name="library_books" size={18} />
            <Text className={styles.itemLabel}>Template</Text>
            <Text className={styles.count}>{templateCount}</Text>
            <Icon name="chevron_right" size={18} className={styles.chevron} />
          </button>
        )}
      </Flex>
    </>
  );
}
